import { _ } from "../../../core/polyglot/polyglot.js";
import {
	badge,
	iconAndText,
	spacer,
	stacker,
	text,
} from "../../../core/elements/index.js";
import {
	copySolidIcon,
	penSolidIcon,
	plusIcon,
	thumbtack,
	trashAltIcon,
} from "../../../core/elements/icons/index.js";
import { formatAsDateAndTime } from "../../../core/helpers/date-formatting-util.js";
import { notBlank } from "../../../core/widgets/forms/validators.js";
import { rightAlign } from "../../../core/elements/elementAlignement.js";
import AccountingDocumentWidget from "../../shared/documentInfrastructure/AccountingDocumentWidget.js";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";

/**
 * Widget rendering published forecast templates.
 *
 * @param {function(): string} getForecastUrl - Return the URL where
 *   the user is expected to go when clicking a forecast template
 * @param {function} onForecastTemplateCreationRequest - Called when trailingWidget is clicked
 *   in order to create a new forecast template
 *@param {function} onForecastTemplateCopyRequest - Called when copy as dialog confirms the copy
 */
export default class ForecastTemplateArchiveDocumentContent extends AccountingDocumentWidget {
	setup({
		getForecastUrl = () => {},
		onForecastTemplateCreationRequest,
		onForecastTemplateCopyRequest,
	}) {
		super.setup(...arguments);

		this._getForecastUrl = getForecastUrl;

		this._onForecastTemplateCreationRequest =
			onForecastTemplateCreationRequest;

		this._onForecastTemplateCopyRequest = onForecastTemplateCopyRequest;

		this._allowChangingMainTemplate = true;

		this._setupForecastTemplates();
	}

	renderContentOn(html) {
		html.div(
			{ class: "ForecastTemplateArchiveDocumentContent" },
			spacer(
				{ direction: "vertical", fullWidth: true, around: true },
				this._buttonSectionRenderer(),
				this._forecastTemplateSectionRenderer(),
			),
		);
	}

	_buttonSectionRenderer() {
		return spacer(
			{ class: "button-section", isBlock: true, align: rightAlign },
			this._createTemplateRenderer(),
		);
	}

	_createTemplateRenderer() {
		return button(
			{
				primary: true,
				onClick: () => this._onForecastTemplateCreationRequest(),
				class: "add-template-button",
				dataTest: "add-new-template",
			},
			iconAndText({ icon: plusIcon }, _("Add new template")),
		);
	}

	_forecastTemplateSectionRenderer() {
		if (!this._forecastTemplates?.length) return (_html) => {};

		return stacker(
			{
				class: "forecast-templates space-large",
				direction: "vertical",
				isBlock: true,
			},
			...this._forecastTemplates.map((forecastTemplate) =>
				this._forecastTemplateRenderer(forecastTemplate),
			),
		);
	}

	_forecastTemplateRenderer(forecastTemplate) {
		return button(
			{
				link: true,
				href: this._getForecastUrl({ forecastTemplate }),
				class: "forecast-template",
				dataTest: "forecast-template",
				fullWidth: true,
			},
			spacer(
				{ fullWidth: true, noWrap: true, spacing: "between" },
				this._forecastTemplateNameRenderer(forecastTemplate),
				this._forecastTemplateDateRenderer(forecastTemplate),
				this._forecastTemplateOptionsRenderer(forecastTemplate),
			),
		);
	}

	_forecastTemplateNameRenderer(forecastTemplate) {
		return spacer(
			{ class: "template-name", dataTest: "template-name", noWrap: true },
			(html) =>
				html.div(
					{ class: "template-name-text-container" },
					forecastTemplate.getName(),
				),
			forecastTemplate.getIsMain() &&
				badge({
					label: iconAndText({ icon: thumbtack }, _("Main")),
				}),
		);
	}

	_forecastTemplateDateRenderer(forecastTemplate) {
		return text(
			{ class: "template-date" },
			_("Last edit: {date}", {
				date: formatAsDateAndTime(forecastTemplate.getLastEditDate()),
			}),
		);
	}

	_forecastTemplateOptionsRenderer(forecastTemplate) {
		return spacer(
			{ class: "template-options", spacing: "before" },
			this._forecastTemplateMainActionRenderer(forecastTemplate),
			this._forecastTemplateCopyAsActionRenderer(forecastTemplate),
			this._forecastTemplateRenameActionRenderer(forecastTemplate),
			this._forecastTemplateDeleteActionRenderer(forecastTemplate),
		);
	}

	_forecastTemplateCopyAsActionRenderer(forecastTemplate) {
		return button(
			{
				link: true,
				title: _("Copy as"),
				dataTest: "copy",
				onClick: (event) => {
					event.preventDefault();
					event.stopPropagation();
					this._showCopyAsDialog({ forecastTemplate });
				},
			},
			copySolidIcon,
		);
	}

	_showCopyAsDialog({ forecastTemplate }) {
		const model = {
			name: `${forecastTemplate.getName()}_Copy`,
			description: forecastTemplate
				.getConfiguration()
				.getForecastTemplateDescription(),
		};

		this._configure({
			title: _("Copy {templateName} as", {
				templateName: forecastTemplate.getName(),
			}),
			okLabel: _("Copy"),
			waitingOkLabel: _("Copying…"),
			formSpec: {
				model,
				saveAction: () =>
					this._onForecastTemplateCopyRequest({
						forecastTemplate,
						model,
					}),
			},
			formBuilder: (form) => {
				form.input({
					name: "name",
					attribute: "name",
					label: _("Template name"),
					validator: notBlank(),
				});

				form.wysiwyg({
					accessor: () => model.description,
					mutator: (value) => (model.description = value),
					attribute: "description",
					label: _("Description"),
					name: "description",
					toolbarAlwaysVisible: true,
				});
			},
		});
	}

	_forecastTemplateRenameActionRenderer(forecastTemplate) {
		return button(
			{
				link: true,
				title: _("Rename"),
				dataTest: "rename",
				onClick: (event) => {
					event.preventDefault();
					event.stopPropagation();
					this._showRenameDialog({ forecastTemplate });
				},
			},
			penSolidIcon,
		);
	}

	_showRenameDialog({ forecastTemplate }) {
		this._configure({
			title: _("Rename"),
			okLabel: _("Confirm"),
			formSpec: {
				model: forecastTemplate,
				saveAction: async () => {
					await this._documentState.updateForecastTemplateName(
						forecastTemplate,
					);

					return this._fetchForecastsAndUpdate();
				},
			},
			formBuilder: (form) => {
				form.input({
					placeHolder: _("Name"),
					label: _("Name"),
					name: "name",
					accessor: forecastTemplate.getName.bind(forecastTemplate),
					mutator: forecastTemplate.setName.bind(forecastTemplate),
					validator: notBlank(),
				});
			},
		});
	}

	_forecastTemplateMainActionRenderer(forecastTemplate) {
		if (forecastTemplate.getIsMain()) return (_html) => {};

		return button(
			{
				link: true,
				title: _("Set as main"),
				dataTest: "set-as-main",
				disabled: !this._allowChangingMainTemplate,
				onClick: async (event) => {
					event.preventDefault();
					event.stopPropagation();

					this._allowChangingMainTemplate = false;
					this.update();

					try {
						await this._documentState.setMainForecastTemplate(
							forecastTemplate,
						);
					} finally {
						this._allowChangingMainTemplate = true;
						this._fetchForecastsAndUpdate();
					}
				},
			},
			thumbtack,
		);
	}

	_forecastTemplateDeleteActionRenderer(forecastTemplate) {
		if (forecastTemplate.getIsMain()) return (_html) => {};

		return button(
			{
				link: true,
				title: _("Delete"),
				dataTest: "delete",
				onClick: (event) => {
					event.preventDefault();
					event.stopPropagation();
					this._showDeleteConfirmationDialog({ forecastTemplate });
				},
			},
			trashAltIcon,
		);
	}

	_showDeleteConfirmationDialog({ forecastTemplate }) {
		this._confirmDeletion({
			name: forecastTemplate.getName(),
			callback: async () => {
				await this._documentState.deleteForecastTemplate(
					forecastTemplate,
				);

				this._fetchForecastsAndUpdate();
			},
		});
	}

	async _fetchForecastsAndUpdate() {
		await this._documentState.fetchForecastTemplates();

		this._setupForecastTemplates();
		this.update();
	}

	_setupForecastTemplates() {
		const mainForecastTemplates = [];
		const otherForecastTemplates = [];

		this._documentState
			.getForecastTemplates()
			.forEach((template) =>
				template.getIsMain()
					? mainForecastTemplates.push(template)
					: otherForecastTemplates.push(template),
			);

		this._forecastTemplates = [
			...mainForecastTemplates,
			...otherForecastTemplates,
		];
	}
}
